import {observer} from 'mobx-react-lite'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {DataObjectId, Hub, Subtask} from 'yougile-model'
import {Sync} from '@client/sync'
import {Icon} from '@components/common/base/icon'
import {useMemoOne} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {Sticker} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'
import {AddExistingOneAsSubtask} from './add-subtask/add-existing-one-as-subtask'
import {CreateSubtaskButton} from './create-subtask-button'
import {Header} from './header'
import {ParentTaskContainer} from './parent-tasks/parent-task-container'
import {SubtaskTaskList} from './subtask-task-list'
import {SubtasksAggregatedInfo} from './subtasks-aggregated-info'
import {StoreContext, SubtasksStore} from './subtasks-store'
import {SyncState} from './sync-state'

interface SubtasksProps {
  taskId: DataObjectId
  afterTaskAdded?: () => void
  hub: Hub
}

const TwRootContainer = tw.div<{$isTaskTemplate?: boolean}>`
    cursor-default
    ${p => (p.$isTaskTemplate ? '' : 'mx-16 pt-16')}
`

const hasDeadline = (isTaskTemplate: boolean, subtasks: Subtask[]): boolean => {
  if (!isTaskTemplate) {
    return false
  }

  const box = Sync.getCurrentSandbox()
  const hasDeadline = subtasks.some(s => {
    for (const sId of Object.keys(s.stickers)) {
      const tag = Sticker.getById(box, sId as DataObjectId)
      if (tag && Sticker.getType(tag) === 'deadline') {
        return true
      }
    }
    return false
  })

  return hasDeadline
}

export const Subtasks = observer((props: SubtasksProps) => {
  const store = useMemoOne(
    () => new SubtasksStore(props.taskId, props.afterTaskAdded),
    [props.taskId]
  )

  const hasSubTasks = store.subtasks.length > 0
  const taskHub = props.hub
  const isTaskTemplate = Task.isTaskTemplate(taskHub)
  const isRepeatTemplate = Task.isRepeatTemplate(taskHub)
  const subsWithDeadline = hasDeadline(isTaskTemplate, store.subtasks)

  return (
    <StoreContext.Provider value={store}>
      <TwRootContainer $isTaskTemplate={isTaskTemplate}>
        {!isTaskTemplate && !isRepeatTemplate && <ParentTaskContainer />}
        <Header
          title={Lang.msg.subtask.subtasks}
          help="subtasks"
          className={cn(isTaskTemplate ? 'h-28 pt-8 pb-4 mb-4' : 'mb-8')}
        />

        {hasSubTasks && (
          <>
            {subsWithDeadline && (
              <div className="flex items-start gap-4 mb-8 cursor-default pb-4">
                <Icon icon="IconCustomAlertCircle" color="status-warning" />
                <div className="text-sm-regular text-secondary">
                  {Lang.msg.taskTemplates.deadlineHint}
                </div>
              </div>
            )}

            {!isTaskTemplate && <SubtasksAggregatedInfo />}
            <SubtaskTaskList
              blocks={false}
              noCheckLists
              isTaskTemplate={isTaskTemplate}
            />
          </>
        )}

        {store.canEdit && (
          <div className="flex flex-col gap-16">
            <CreateSubtaskButton withoutHover />

            {!isTaskTemplate && <AddExistingOneAsSubtask />}
          </div>
        )}
      </TwRootContainer>
      <SyncState />
    </StoreContext.Provider>
  )
})
